/**
 * Product Gallery Fullscreen Modal with Swiper, Native-like Smooth Zoom, and Responsive Controls
 * High Performance Edition:
 * - Lazy Template Mount (0 DOM elements & 0 Swiper instances on initial page load)
 * - Fluid Pinch-to-Zoom (iOS/Android), double-tap to zoom, pan with bounce, no single-tap zoom on touch
 * - Active-only event listeners (scroll, keydown, resize)
 */
(function () {
  'use strict';

  function initProductGalleryModal(sectionId) {
    if (!sectionId) return;

    const mainGallery = document.getElementById(`MediaGallery-${sectionId}`);
    const template = document.getElementById(`ProductGalleryModalTemplate-${sectionId}`);
    const mount = document.getElementById(`ProductGalleryModalMount-${sectionId}`);

    if (!mainGallery && !template) return;

    // Track active zoom states for cleanup
    const zoomResetters = [];
    let isInitialized = false;

    function ensureMounted() {
      let modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      if (modal) return modal;

      if (template && mount && !mount.querySelector('.product-gallery-modal')) {
        mount.appendChild(template.content.cloneNode(true));
        modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      }
      return modal;
    }

    function getHeaderBottom() {
      const headerOuter = document.querySelector('.site-header__outer-wrapper') || document.querySelector('.site-header') || document.querySelector('header');
      if (headerOuter) {
        const hRect = headerOuter.getBoundingClientRect();
        return Math.max(0, Math.round(hRect.bottom));
      }
      return 100;
    }

    function updateDesktopModalBounds() {
      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      if (!modal) return;

      if (window.innerWidth <= 768) {
        modal.style.position = 'fixed';
        modal.style.left = '0px';
        modal.style.width = '100vw';
        modal.style.top = '0px';
        modal.style.height = '100vh';
        modal.style.height = '100dvh';
        return;
      }

      const mediaContainer = (mainGallery && mainGallery.closest('.media-wrapper')) || mainGallery;
      if (mediaContainer) {
        const rect = mediaContainer.getBoundingClientRect();
        const headerBottom = getHeaderBottom();
        const topOffset = headerBottom > 0 ? headerBottom : 100;
        const bottomPadding = 25;

        modal.style.position = 'fixed';
        modal.style.left = `${rect.left}px`;
        modal.style.width = `${rect.width}px`;
        modal.style.top = `${topOffset}px`;
        modal.style.height = `calc(100vh - ${topOffset + bottomPadding}px)`;
        modal.style.height = `calc(100dvh - ${topOffset + bottomPadding}px)`;
      }
    }

    function updateBullets() {
      const modalSwiper = document.getElementById(`Slider-Modal-${sectionId}`);
      const bulletsContainer = document.getElementById(`ProductGalleryModalBullets-${sectionId}`);
      if (!bulletsContainer || !modalSwiper) return;
      const slides = modalSwiper.querySelectorAll('swiper-slide');
      const count = slides.length;

      const footer = document.getElementById(`ProductGalleryModalFooter-${sectionId}`);
      if (window.innerWidth > 768) {
        if (footer) footer.style.display = 'none';
        return;
      }
      if (count <= 1) {
        if (footer) footer.style.display = 'none';
        return;
      }
      if (footer) footer.style.display = 'flex';

      // Re-generate bullets if count changed (e.g. variant switched)
      if (bulletsContainer.children.length !== count) {
        bulletsContainer.innerHTML = '';
        for (let i = 0; i < count; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = `product-gallery-modal__bullet${i === 0 ? ' is-active' : ''}`;
          b.dataset.index = i;
          b.setAttribute('aria-label', `Foto ${i + 1}`);
          b.addEventListener('click', (e) => {
            e.stopPropagation();
            if (modalSwiper.swiper) modalSwiper.swiper.slideTo(i);
          });
          bulletsContainer.appendChild(b);
        }
      }

      const activeIdx = modalSwiper.swiper ? modalSwiper.swiper.activeIndex || 0 : 0;
      Array.from(bulletsContainer.children).forEach((b, idx) => {
        b.classList.toggle('is-active', idx === activeIdx);
      });
    }

    function pauseAllVideos() {
      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      if (!modal) return;
      modal.querySelectorAll('video').forEach(video => {
        try { video.pause(); } catch (err) { }
      });
      modal.querySelectorAll('iframe').forEach(iframe => {
        try {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } catch (err) { }
      });
    }

    function resetAllZooms() {
      zoomResetters.forEach(fn => fn());
    }

    // Active Modal Event Handlers (Attached only while modal is active)
    function onPageScroll() {
      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      if (!modal || !modal.classList.contains('is-active')) return;

      // Auto-close if scrolled to TryptichSection
      const tryptich = document.querySelector('[id^="TryptichSection-"]') || document.querySelector('.section-product-tryptich');
      if (tryptich) {
        const tRect = tryptich.getBoundingClientRect();
        if (tRect.top <= window.innerHeight * 0.8) {
          closeModal();
          return;
        }
      }

      // Auto-close if main-product gallery scrolled out of view completely
      const mediaContainer = (mainGallery && mainGallery.closest('.media-wrapper')) || mainGallery;
      if (mediaContainer) {
        const mRect = mediaContainer.getBoundingClientRect();
        if (mRect.bottom <= 120) {
          closeModal();
          return;
        }
      }

      // Dynamically adjust top offset under header when scrolling
      if (window.innerWidth > 768) {
        updateDesktopModalBounds();
      }
    }

    function onWindowResize() {
      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      const modalSwiper = document.getElementById(`Slider-Modal-${sectionId}`);
      if (modal && modal.classList.contains('is-active')) {
        updateDesktopModalBounds();
        if (modalSwiper && modalSwiper.swiper) modalSwiper.swiper.update();
      }
    }

    function onKeydown(e) {
      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      const modalSwiper = document.getElementById(`Slider-Modal-${sectionId}`);
      if (!modal || !modal.classList.contains('is-active')) return;
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowLeft') {
        if (modalSwiper && modalSwiper.swiper) modalSwiper.swiper.slidePrev();
      } else if (e.key === 'ArrowRight') {
        if (modalSwiper && modalSwiper.swiper) modalSwiper.swiper.slideNext();
      }
    }

    function onOutsideClick(e) {
      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      if (!modal || !modal.classList.contains('is-active')) return;
      if (modal.querySelector('.product-gallery-modal__zoom-wrapper.is-zoomed')) return;
      if (!modal.contains(e.target) && !e.target.closest(`#MediaGallery-${sectionId}`)) {
        closeModal();
      }
    }

    function attachActiveListeners() {
      window.addEventListener('scroll', onPageScroll, { passive: true });
      window.addEventListener('resize', onWindowResize, { passive: true });
      window.addEventListener('keydown', onKeydown);
      setTimeout(() => {
        document.addEventListener('click', onOutsideClick);
      }, 50);
    }

    function detachActiveListeners() {
      window.removeEventListener('scroll', onPageScroll);
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('keydown', onKeydown);
      document.removeEventListener('click', onOutsideClick);
    }

    function openModal(mediaId) {
      ensureMounted();
      ensureInitialized();

      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      const modalSwiper = document.getElementById(`Slider-Modal-${sectionId}`);
      if (!modal || !modalSwiper) return;

      updateDesktopModalBounds();
      modal.classList.add('is-active');
      attachActiveListeners();

      // Find target slide by data-media-id
      const modalSlides = Array.from(modalSwiper.querySelectorAll('swiper-slide'));
      let targetIndex = 0;
      if (mediaId) {
        const found = modalSlides.findIndex(s => s.dataset.mediaId === mediaId);
        if (found >= 0) targetIndex = found;
      }

      const goToSlide = () => {
        if (modalSwiper.swiper) {
          modalSwiper.swiper.update();
          modalSwiper.swiper.slideTo(targetIndex, 0);
          updateBullets();
        }
      };

      if (modalSwiper.swiper) {
        goToSlide();
      } else {
        requestAnimationFrame(() => {
          setTimeout(goToSlide, 40);
        });
      }
    }

    function closeModal() {
      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      if (modal) modal.classList.remove('is-active');
      detachActiveListeners();
      resetAllZooms();
      pauseAllVideos();
    }

    // Lazy initialization of controls and zoom
    function ensureInitialized() {
      if (isInitialized) return;
      ensureMounted();

      const modal = document.getElementById(`ProductGalleryModal-${sectionId}`);
      const modalSwiper = document.getElementById(`Slider-Modal-${sectionId}`);
      if (!modal || !modalSwiper) return;

      isInitialized = true;

      const closeBtn = document.getElementById(`ProductGalleryModalClose-${sectionId}`);
      const prevBtn = document.getElementById(`ProductGalleryModalPrev-${sectionId}`);
      const nextBtn = document.getElementById(`ProductGalleryModalNext-${sectionId}`);

      // Close button
      if (closeBtn && !closeBtn.dataset.bound) {
        closeBtn.dataset.bound = 'true';
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          closeModal();
        });
      }

      // Prev / Next buttons
      if (prevBtn && !prevBtn.dataset.bound) {
        prevBtn.dataset.bound = 'true';
        prevBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (modalSwiper.swiper) modalSwiper.swiper.slidePrev();
        });
      }

      if (nextBtn && !nextBtn.dataset.bound) {
        nextBtn.dataset.bound = 'true';
        nextBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (modalSwiper.swiper) modalSwiper.swiper.slideNext();
        });
      }

      // Backdrop click
      if (!modal.dataset.boundBackdrop) {
        modal.dataset.boundBackdrop = 'true';
        modal.addEventListener('click', (e) => {
          if (
            e.target === modal ||
            e.target.classList.contains('product-gallery-modal__slide-inner') ||
            e.target.tagName === 'SWIPER-SLIDE' ||
            e.target.tagName === 'SWIPER-CONTAINER'
          ) {
            closeModal();
          }
        });
      }

      // Swiper slide change
      modalSwiper.removeEventListener('swiperslidechange', modalSwiper._onSlideChange);
      modalSwiper._onSlideChange = () => {
        resetAllZooms();
        pauseAllVideos();
        updateBullets();
      };
      modalSwiper.addEventListener('swiperslidechange', modalSwiper._onSlideChange);

      setupZoomControllers();
      updateBullets();
    }

    // Setup Fluid Pinch-to-Zoom, Double-Tap, Pan, and Desktop Click Zoom
    function setupZoomControllers() {
      zoomResetters.length = 0;
      const modalSwiper = document.getElementById(`Slider-Modal-${sectionId}`);
      if (!modalSwiper) return;

      const zoomWrappers = modalSwiper.querySelectorAll('[data-zoom-wrapper]');

      zoomWrappers.forEach(wrapper => {
        const img = wrapper.querySelector('img');
        if (!img) return;

        let scale = 1;
        let translateX = 0;
        let translateY = 0;

        // Mobile touch state
        let isPanning = false;
        let isPinching = false;
        let touchStartX = 0;
        let touchStartY = 0;
        let initialTranslateX = 0;
        let initialTranslateY = 0;

        let pinchStartDist = 0;
        let pinchStartScale = 1;
        let pinchStartMidX = 0;
        let pinchStartMidY = 0;

        // Double tap detection
        let lastTapTime = 0;
        let lastTapX = 0;
        let lastTapY = 0;

        // Desktop mouse pan state
        let isMouseDown = false;
        let isMouseDragging = false;
        let mouseStartX = 0;
        let mouseStartY = 0;

        const getBounds = (s) => {
          const wrapRect = wrapper.getBoundingClientRect();
          const iw = (img.naturalWidth && img.offsetWidth) ? img.offsetWidth : wrapRect.width;
          const ih = (img.naturalHeight && img.offsetHeight) ? img.offsetHeight : wrapRect.height;

          const scaledW = iw * s;
          const scaledH = ih * s;

          const maxX = Math.max(0, (scaledW - wrapRect.width) / 2);
          const minX = -maxX;
          const maxY = Math.max(0, (scaledH - wrapRect.height) / 2);
          const minY = -maxY;

          return { minX, maxX, minY, maxY };
        };

        const reset = () => {
          scale = 1;
          translateX = 0;
          translateY = 0;
          isPanning = false;
          isPinching = false;
          isMouseDown = false;
          isMouseDragging = false;

          img.style.transition = 'transform 0.28s cubic-bezier(0.2, 0, 0.2, 1)';
          img.style.transform = 'translate3d(0px, 0px, 0px) scale(1)';
          wrapper.classList.remove('is-zoomed', 'is-panning');

          const slide = wrapper.closest('swiper-slide');
          if (slide) slide.classList.remove('swiper-no-swiping');
          if (modalSwiper.swiper) modalSwiper.swiper.allowTouchMove = true;
        };

        const zoomInAt = (clientX, clientY) => {
          scale = 2.4;
          const wrapRect = wrapper.getBoundingClientRect();
          const tapOffsetX = clientX - (wrapRect.left + wrapRect.width / 2);
          const tapOffsetY = clientY - (wrapRect.top + wrapRect.height / 2);

          const { minX, maxX, minY, maxY } = getBounds(scale);

          // Center on tapped position
          const targetX = -tapOffsetX * (scale - 1);
          const targetY = -tapOffsetY * (scale - 1);

          translateX = Math.max(minX, Math.min(maxX, targetX));
          translateY = Math.max(minY, Math.min(maxY, targetY));

          img.style.transition = 'transform 0.28s cubic-bezier(0.2, 0, 0.2, 1)';
          img.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px) scale(${scale})`;
          wrapper.classList.add('is-zoomed');

          const slide = wrapper.closest('swiper-slide');
          if (slide) slide.classList.add('swiper-no-swiping');
          if (modalSwiper.swiper) modalSwiper.swiper.allowTouchMove = false;
        };

        // Desktop ONLY Click to Toggle Zoom (NEVER fires on mobile touch single-tap)
        wrapper.addEventListener('click', (e) => {
          // If on touch device or touch pointer, ignore single tap completely
          if (e.pointerType === 'touch' || ('ontouchstart' in window && window.innerWidth <= 768)) {
            return;
          }
          if (isMouseDragging) return;

          if (scale > 1.05) {
            reset();
          } else {
            zoomInAt(e.clientX, e.clientY);
          }
        });

        // Desktop Mouse Pan (Drag)
        wrapper.addEventListener('mousedown', (e) => {
          if (scale <= 1.05) return;
          e.preventDefault();
          isMouseDown = true;
          isMouseDragging = false;
          mouseStartX = e.clientX;
          mouseStartY = e.clientY;
          initialTranslateX = translateX;
          initialTranslateY = translateY;
          img.style.transition = 'none';
          wrapper.classList.add('is-panning');

          const onMouseMove = (ev) => {
            if (!isMouseDown) return;
            const dx = ev.clientX - mouseStartX;
            const dy = ev.clientY - mouseStartY;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
              isMouseDragging = true;
            }
            const { minX, maxX, minY, maxY } = getBounds(scale);
            translateX = Math.max(minX, Math.min(maxX, initialTranslateX + dx));
            translateY = Math.max(minY, Math.min(maxY, initialTranslateY + dy));
            img.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px) scale(${scale})`;
          };

          const onMouseUp = () => {
            isMouseDown = false;
            wrapper.classList.remove('is-panning');
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            setTimeout(() => { isMouseDragging = false; }, 50);
          };

          window.addEventListener('mousemove', onMouseMove);
          window.addEventListener('mouseup', onMouseUp);
        });

        // Mobile Touch Gestures: Double-Tap, Fluid Pinch-to-Zoom, and Smooth 2D Pan
        wrapper.addEventListener('touchstart', (e) => {
          if (e.touches.length === 1) {
            const touch = e.touches[0];
            const now = Date.now();
            const timeDiff = now - lastTapTime;
            const distDiff = Math.hypot(touch.clientX - lastTapX, touch.clientY - lastTapY);

            // Double Tap Detection: requires two taps within 320ms and < 35px
            if (timeDiff > 40 && timeDiff < 320 && distDiff < 35) {
              e.preventDefault();
              e.stopPropagation();
              lastTapTime = 0;
              if (scale > 1.05) {
                reset();
              } else {
                zoomInAt(touch.clientX, touch.clientY);
              }
              return;
            }

            // First tap: record for potential double-tap
            lastTapTime = now;
            lastTapX = touch.clientX;
            lastTapY = touch.clientY;

            // If already zoomed, prepare 1-finger panning
            if (scale > 1.05) {
              isPanning = true;
              touchStartX = touch.clientX;
              touchStartY = touch.clientY;
              initialTranslateX = translateX;
              initialTranslateY = translateY;
              img.style.transition = 'none';
              wrapper.classList.add('is-panning');
            }
          } else if (e.touches.length === 2) {
            // Cancel double-tap
            lastTapTime = 0;
            isPinching = true;
            isPanning = false;

            // Stop Swiper from capturing gesture
            const slide = wrapper.closest('swiper-slide');
            if (slide) slide.classList.add('swiper-no-swiping');
            if (modalSwiper.swiper) modalSwiper.swiper.allowTouchMove = false;

            pinchStartDist = Math.hypot(
              e.touches[0].clientX - e.touches[1].clientX,
              e.touches[0].clientY - e.touches[1].clientY
            );
            pinchStartScale = scale;
            pinchStartMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
            pinchStartMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
            initialTranslateX = translateX;
            initialTranslateY = translateY;
            img.style.transition = 'none';
          }
        }, { passive: false });

        wrapper.addEventListener('touchmove', (e) => {
          // If 1 finger moved significantly while not zoomed, cancel double tap (user is swiping carousel)
          if (e.touches.length === 1 && scale <= 1.05) {
            const dist = Math.hypot(e.touches[0].clientX - lastTapX, e.touches[0].clientY - lastTapY);
            if (dist > 12) {
              lastTapTime = 0;
            }
          }

          // 2-Finger Fluid Pinch to Zoom
          if (isPinching && e.touches.length === 2) {
            e.preventDefault();
            e.stopPropagation();

            const currentDist = Math.hypot(
              e.touches[0].clientX - e.touches[1].clientX,
              e.touches[0].clientY - e.touches[1].clientY
            );

            if (pinchStartDist > 0) {
              const factor = currentDist / pinchStartDist;
              // Smooth scale range with rubber-band bounds
              scale = Math.max(0.75, Math.min(4.5, pinchStartScale * factor));

              const curMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
              const curMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

              translateX = initialTranslateX + (curMidX - pinchStartMidX);
              translateY = initialTranslateY + (curMidY - pinchStartMidY);

              img.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px) scale(${scale})`;
              if (scale > 1.05) {
                wrapper.classList.add('is-zoomed');
              }
            }
          } else if (isPanning && e.touches.length === 1 && scale > 1.05) {
            // 1-Finger Pan when zoomed
            e.preventDefault();
            e.stopPropagation();

            const dx = e.touches[0].clientX - touchStartX;
            const dy = e.touches[0].clientY - touchStartY;

            const { minX, maxX, minY, maxY } = getBounds(scale);

            let curX = initialTranslateX + dx;
            let curY = initialTranslateY + dy;

            // Elastic resistance when dragging past edges
            if (curX > maxX) curX = maxX + (curX - maxX) * 0.25;
            else if (curX < minX) curX = minX + (curX - minX) * 0.25;

            if (curY > maxY) curY = maxY + (curY - maxY) * 0.25;
            else if (curY < minY) curY = minY + (curY - minY) * 0.25;

            translateX = curX;
            translateY = curY;
            img.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px) scale(${scale})`;
          }
        }, { passive: false });

        wrapper.addEventListener('touchend', (e) => {
          wrapper.classList.remove('is-panning');

          if (isPinching) {
            // If fingers lifted below threshold, smooth bounce back to normal 1x
            if (scale < 1.1) {
              reset();
            } else {
              isPinching = false;
              if (scale > 3.5) scale = 3.5;
              const { minX, maxX, minY, maxY } = getBounds(scale);
              translateX = Math.max(minX, Math.min(maxX, translateX));
              translateY = Math.max(minY, Math.min(maxY, translateY));
              img.style.transition = 'transform 0.25s cubic-bezier(0.2, 0, 0.2, 1)';
              img.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px) scale(${scale})`;
              wrapper.classList.add('is-zoomed');
              const slide = wrapper.closest('swiper-slide');
              if (slide) slide.classList.add('swiper-no-swiping');
              if (modalSwiper.swiper) modalSwiper.swiper.allowTouchMove = false;
            }
            return;
          }

          if (isPanning) {
            isPanning = false;
            // Snap back smoothly into bounds if dragged past boundaries
            const { minX, maxX, minY, maxY } = getBounds(scale);
            const clampedX = Math.max(minX, Math.min(maxX, translateX));
            const clampedY = Math.max(minY, Math.min(maxY, translateY));

            if (clampedX !== translateX || clampedY !== translateY) {
              translateX = clampedX;
              translateY = clampedY;
              img.style.transition = 'transform 0.25s cubic-bezier(0.2, 0, 0.2, 1)';
              img.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px) scale(${scale})`;
            }
          }
        });

        zoomResetters.push(reset);
      });
    }

    // Main Gallery Click Trigger (Bound strictly to Slider-Gallery, excluding lower thumbnails)
    const mainSwiperEl = mainGallery && mainGallery.querySelector(`swiper-container[id^="Slider-Gallery-"]`);
    if (mainSwiperEl && !mainSwiperEl.dataset.boundModalTrigger) {
      mainSwiperEl.dataset.boundModalTrigger = 'true';

      // Pre-warm modal strictly on desktop hover or when browser is idle (never on touchstart)
      const prewarm = () => {
        ensureMounted();
        ensureInitialized();
      };
      mainSwiperEl.addEventListener('pointerenter', prewarm, { once: true, passive: true });
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          setTimeout(prewarm, 2500);
        });
      }

      let touchStartX = 0;
      let touchStartY = 0;
      let isTouchMoved = false;

      mainSwiperEl.addEventListener('touchstart', (e) => {
        if (e.touches.length === 1) {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
          isTouchMoved = false;
        }
      }, { passive: true });

      mainSwiperEl.addEventListener('touchmove', (e) => {
        if (e.touches.length === 1) {
          const dx = Math.abs(e.touches[0].clientX - touchStartX);
          const dy = Math.abs(e.touches[0].clientY - touchStartY);
          if (dx > 10 || dy > 10) {
            isTouchMoved = true;
          }
        }
      }, { passive: true });

      mainSwiperEl.addEventListener('click', (e) => {
        if (isTouchMoved) {
          isTouchMoved = false;
          return;
        }

        // Never open from lower gallery / thumbnails
        if (e.target.closest('.thumbnails, [id^="Slider-Thumb-"]')) {
          return;
        }

        if (e.target.closest('.deferred-media__poster-button, video, button.swiper-button-prev, button.swiper-button-next, .product__xr-button')) {
          return;
        }

        // Strictly require click on or inside .product__media
        const mediaContainer = e.target.closest('.product__media');
        if (!mediaContainer) return;

        if (mainSwiperEl.swiper && mainSwiperEl.swiper.touchEventsData && mainSwiperEl.swiper.touchEventsData.isMoved) {
          return;
        }

        const slide = mediaContainer.closest('swiper-slide');
        if (!slide) return;

        openModal(slide.dataset.mediaId);
      });
    }

    // Expose trigger on mount container for external variant triggers if needed
    if (mount) {
      mount._resetModal = () => {
        isInitialized = false;
        mount.innerHTML = '';
      };
    }
  }

  // Initialize gallery trigger on DOM ready (minimal work)
  document.addEventListener('DOMContentLoaded', () => {
    const templates = document.querySelectorAll('[id^="ProductGalleryModalTemplate-"]');
    templates.forEach(t => {
      const match = t.id.match(/^ProductGalleryModalTemplate-(.+)$/);
      if (match && match[1]) {
        initProductGalleryModal(match[1]);
      }
    });

    // Fallback for directly rendered modal if template not used
    const modals = document.querySelectorAll('.product-gallery-modal');
    modals.forEach(m => {
      const match = m.id.match(/^ProductGalleryModal-(.+)$/);
      if (match && match[1]) {
        initProductGalleryModal(match[1]);
      }
    });
  });

  // Re-bind when variant changes
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const sectionId = event.data?.sectionId;
      if (sectionId) {
        setTimeout(() => {
          const mount = document.getElementById(`ProductGalleryModalMount-${sectionId}`);
          if (mount && mount._resetModal) {
            mount._resetModal();
          }
          initProductGalleryModal(sectionId);
        }, 50);
      }
    });
  }
})();
